
// Register a fallback Trusted Types default policy to prevent third-party scripts from crashing when require-trusted-types-for is enabled.
try {
  if (typeof window !== "undefined" && window.trustedTypes) {
    if (!window.trustedTypes.defaultPolicy) {
      window.trustedTypes.createPolicy("default", {
        createHTML: (string) => string,
        createScript: (string) => string,
        createScriptURL: (string) => string,
      });
    }
  }
} catch (e) {
  console.warn("Failed to create Trusted Types fallback policy:", e);
}

// Shim for deprecated Storage APIs (webkitStorageInfo, webkitTemporaryStorage, webkitPersistentStorage)
// to prevent browser deprecation warnings triggered by third-party scripts (e.g., Cloudflare Bot Management).
// This maps the deprecated APIs to the standardized navigator.storage API.
try {
  if (typeof window !== "undefined") {
    const hasWebkitStorageInfo = "webkitStorageInfo" in window;
    if (hasWebkitStorageInfo) {
      const mockStorageInfo = {
        TEMPORARY: 0,
        PERSISTENT: 1,
        queryUsageAndQuota: function(type, successCallback, errorCallback) {
          if (navigator.storage && navigator.storage.estimate) {
            navigator.storage.estimate()
              .then(estimate => {
                if (successCallback) successCallback(estimate.usage || 0, estimate.quota || 0);
              })
              .catch(err => {
                if (errorCallback) errorCallback(err);
              });
          } else if (successCallback) {
            successCallback(0, 0);
          }
        },
        requestQuota: function(type, newQuotaInBytes, successCallback, errorCallback) {
          if (type === 1 && navigator.storage && navigator.storage.persist) {
            navigator.storage.persist()
              .then(() => {
                if (successCallback) successCallback(newQuotaInBytes);
              })
              .catch(err => {
                if (errorCallback) errorCallback(err);
              });
          } else if (successCallback) {
            successCallback(newQuotaInBytes);
          }
        }
      };

      try {
        Object.defineProperty(window, "webkitStorageInfo", {
          get: () => mockStorageInfo,
          configurable: true,
          enumerable: true
        });
      } catch (e) {
        window.webkitStorageInfo = mockStorageInfo;
      }
    }

    if (typeof navigator !== "undefined") {
      const mockTemporaryStorage = {
        queryUsageAndQuota: function(successCallback, errorCallback) {
          if (navigator.storage && navigator.storage.estimate) {
            navigator.storage.estimate()
              .then(estimate => {
                if (successCallback) successCallback(estimate.usage || 0, estimate.quota || 0);
              })
              .catch(err => {
                if (errorCallback) errorCallback(err);
              });
          } else if (successCallback) {
            successCallback(0, 0);
          }
        },
        requestQuota: function(newQuotaInBytes, successCallback, errorCallback) {
          if (successCallback) successCallback(newQuotaInBytes);
        }
      };

      const mockPersistentStorage = {
        queryUsageAndQuota: function(successCallback, errorCallback) {
          if (navigator.storage && navigator.storage.estimate) {
            navigator.storage.estimate()
              .then(estimate => {
                if (successCallback) successCallback(estimate.usage || 0, estimate.quota || 0);
              })
              .catch(err => {
                if (errorCallback) errorCallback(err);
              });
          } else if (successCallback) {
            successCallback(0, 0);
          }
        },
        requestQuota: function(newQuotaInBytes, successCallback, errorCallback) {
          if (navigator.storage && navigator.storage.persist) {
            navigator.storage.persist()
              .then(() => {
                if (successCallback) successCallback(newQuotaInBytes);
              })
              .catch(err => {
                if (errorCallback) errorCallback(err);
              });
          } else if (successCallback) {
            successCallback(newQuotaInBytes);
          }
        }
      };

      if ("webkitTemporaryStorage" in navigator) {
        try {
          Object.defineProperty(navigator, "webkitTemporaryStorage", {
            get: () => mockTemporaryStorage,
            configurable: true,
            enumerable: true
          });
        } catch (e) {
          navigator.webkitTemporaryStorage = mockTemporaryStorage;
        }
      }

      if ("webkitPersistentStorage" in navigator) {
        try {
          Object.defineProperty(navigator, "webkitPersistentStorage", {
            get: () => mockPersistentStorage,
            configurable: true,
            enumerable: true
          });
        } catch (e) {
          navigator.webkitPersistentStorage = mockPersistentStorage;
        }
      }
    }
  }
} catch (e) {
  // Silent fallback
}

import "./controllers"

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason
  if (reason && (reason.name === "AbortError" || /aborted/i.test(reason.message || ""))) {
    event.preventDefault()
  }
})

class Modal {
  constructor(el) {
    this.el = el
  }

  static getOrCreateInstance(el) {
    if (el.modalInstance) {
      return el.modalInstance
    }
    return new Modal(el)
  }

  show(relatedTarget = null) {
    if (this.el.modalInstance) {
      this.el.modalInstance.show(relatedTarget)
      return
    }

    if (!this.el.hasAttribute("data-controller") || !this.el.getAttribute("data-controller").includes("modal")) {
      const controllers = this.el.getAttribute("data-controller") || ""
      this.el.setAttribute("data-controller", `${controllers} modal`.trim())
    }

    const getController = () => {
      if (this.el.modalInstance) {
        this.el.modalInstance.show(relatedTarget)
        return true
      }
      if (window.Stimulus) {
        try {
          const controller = window.Stimulus.getControllerForElementAndIdentifier(this.el, "modal")
          if (controller) {
            this.el.modalInstance = controller
            controller.show(relatedTarget)
            return true
          }
        } catch (e) {
        }
      }
      return false
    }

    if (!getController()) {
      const interval = setInterval(() => {
        if (getController()) clearInterval(interval)
      }, 10)
      setTimeout(() => clearInterval(interval), 500)
    }
  }

  hide() {
    if (this.el.modalInstance) {
      this.el.modalInstance.hide()
      return
    }

    const getController = () => {
      if (this.el.modalInstance) {
        this.el.modalInstance.hide()
        return true
      }
      if (window.Stimulus) {
        try {
          const controller = window.Stimulus.getControllerForElementAndIdentifier(this.el, "modal")
          if (controller) {
            this.el.modalInstance = controller
            controller.hide()
            return true
          }
        } catch (e) {
        }
      }
      return false
    }

    if (!getController()) {
      const interval = setInterval(() => {
        if (getController()) clearInterval(interval)
      }, 10)
      setTimeout(() => clearInterval(interval), 500)
    }
  }

  toggle(relatedTarget = null) {
    if (this.el.classList.contains("show")) {
      this.hide()
    } else {
      this.show(relatedTarget)
    }
  }
}

window.Modal = Modal
window.bootstrap = { Modal }

document.addEventListener("turbo:before-render", () => {
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove())
  document.body.classList.remove("modal-open")
})

document.addEventListener("DOMContentLoaded", () => {
  const autoModalElement = document.getElementById("autoappointmentModal")
  const appointmentModalElement = document.getElementById("appointmentModal")

  if (autoModalElement && sessionStorage.getItem("promoModalShown") !== "true") {
    setTimeout(() => {
      if (autoModalElement.classList.contains("show")) return
      if (appointmentModalElement && appointmentModalElement.classList.contains("show")) return

      sessionStorage.setItem("promoModalShown", "true")
      Modal.getOrCreateInstance(autoModalElement).show()
    }, 60000)
  }
})

document.addEventListener("DOMContentLoaded", () => {
  const counters = document.querySelectorAll(".counter")

  function initCounters() {
    if (!("IntersectionObserver" in window)) return
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const counter = entry.target
          const target = parseInt(counter.innerText, 10) || 0
          const step = target / 200
          let current = 0
          const timer = setInterval(() => {
            current += step
            counter.innerText = Math.min(Math.floor(current), target)
            if (current >= target) clearInterval(timer)
          }, 10)
          observer.unobserve(counter)
        }
      })
    })
    counters.forEach((c) => observer.observe(c))
  }

  initCounters()
})

// Intercept clicks on links containing '#' to smoothly scroll to targets
// without adding or updating the URL hash in the browser address bar.
document.addEventListener("click", (e) => {
  const link = e.target.closest("a");
  if (!link) return;

  const href = link.getAttribute("href");
  if (!href) return;

  // In-page hash links must never write the hash into the address bar (no
  // "#section-specialities" trailing the URL). Always preventDefault for
  // same-page anchors and scroll manually when a target exists.
  if (href.startsWith("#")) {
    // Pure hash link like href="#assist-form" (or a bare href="#").
    e.preventDefault();
    const targetId = href.slice(1);
    if (targetId) {
      const target = document.getElementById(targetId);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  } else if (href.includes("#")) {
    // Path-based hash link like href="/gurgaon/procedures/knee-replacement#assist-form".
    const [path, hash] = href.split("#");
    if (hash && (path === "" || path === window.location.pathname)) {
      // Same-page section link — scroll, but keep the hash out of the URL.
      e.preventDefault();
      const target = document.getElementById(hash);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
});

// Paid (gclid) pages are a single-conversion funnel: every link outside the
// footer is disabled and instead opens the appointment popup. Footer links,
// in-page anchors, tel:/mailto:, and existing modal triggers stay live. Capture
// phase so we intercept before navigation and before bubble-phase handlers.
document.addEventListener("click", (e) => {
  if (!document.body.classList.contains("is-paid")) return;

  const link = e.target.closest("a");
  if (!link) return;

  // Footer is intentionally left functional.
  if (link.closest("footer")) return;

  // Opt-out: links inside a [data-paid-allow] element navigate normally even on
  // paid pages (e.g. hospital listing cards that must reach the doctors page).
  if (link.closest("[data-paid-allow]")) return;

  // Cards / buttons that already open a modal are handled by the modal controller.
  if (link.matches('[data-bs-toggle="modal"], [data-modal-toggle]')) return;

  const href = link.getAttribute("href");
  if (!href) return;

  // Allow non-navigation links: in-page anchors, phone, email, JS hooks.
  if (href.startsWith("#") || /^(tel:|mailto:|javascript:)/i.test(href)) return;

  // Any remaining link navigates away from the funnel — block it and open the
  // booking popup instead.
  e.preventDefault();
  e.stopPropagation();

  const modal = document.getElementById("appointmentModal");
  if (modal && window.Modal) {
    window.Modal.getOrCreateInstance(modal).show(link);
  }
}, true);
